/**
 * useAccountModules — per-account module access from Prospera-Corp-App's
 * Module Settings (via api-portal `GET /account/modules`).
 *
 * Fail-open by design: while loading or on error everything is treated as
 * enabled, so a flaky endpoint can never lock staff out of the app.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccountModules, type AccountModule } from '@/services/accountModules.service';
import { useAuthStore } from '@/store/authStore';

export function useAccountModules() {
  const accountId = useAuthStore((s) => s.accountId);
  const storedModules = useAuthStore((s) => s.modules);

  return useQuery({
    queryKey: ['account', 'modules', accountId],
    queryFn: getAccountModules,
    enabled: !!accountId,
    // Seed from the login//auth/me payload persisted in authStore so
    // module-gated nav filters on the very first paint (restricted accounts).
    // initialDataUpdatedAt: 0 marks the seed stale → an immediate background
    // refetch still runs, so Corp-App toggles are never masked by the seed.
    initialData: storedModules ? { modules: storedModules } : undefined,
    initialDataUpdatedAt: storedModules ? 0 : undefined,
    // Short enough that a Module Settings toggle shows up on the next
    // navigation/focus without a hard reload.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Freshness rule for the fail-CLOSED `explicit` signal: the modules data is
 * "fresh" only when a real `/account/modules` fetch succeeded this session
 * (`dataUpdatedAt > 0` — the authStore seed alone leaves it at 0 because of
 * `initialDataUpdatedAt: 0`) and the latest attempt did not error after that
 * success (`errorUpdatedAt > dataUpdatedAt` means the newest fetch failed —
 * React Query keeps serving the cached data, so status alone can't tell).
 * The fail-open `enabled` signal deliberately does NOT use this — the seed's
 * anti-flicker purpose stays intact for everything else.
 */
export function isModulesFresh(q: { dataUpdatedAt: number; errorUpdatedAt: number }): boolean {
  return q.dataUpdatedAt > 0 && !(q.errorUpdatedAt > q.dataUpdatedAt);
}

export interface ModuleAccess {
  /** False only once the API has positively said the module is off. */
  enabled: boolean;
  /**
   * Fail-CLOSED signal: true only after a FRESH successful `/account/modules`
   * response this session (see `isModulesFresh`) reported an explicitly
   * ACTIVE aa_accounts_modules row for the key. False while loading, on
   * error, when only the persisted authStore seed is available, or when the
   * key is absent — opt-in surfaces (e.g. Vibrant ordering) must render only
   * when this is true, so a stale seed can never keep a revoked module open.
   */
  explicit: boolean;
  /**
   * Fail-toward-ON twin of `explicit`: true when ANY copy of the modules data
   * says the module is explicitly active, including the persisted authStore
   * seed and the cache React Query keeps serving after a failed refetch. For
   * enforcement only (e.g. ScriptSure's field limits), where wrongly applying
   * a rule is harmless and wrongly skipping it is not. Never use it to SHOW an
   * opt-in surface; that is what the freshness-gated `explicit` is for.
   */
  explicitEvenIfStale: boolean;
  /** Section slugs hidden for this account (route segment under the module). */
  disabledSections: string[];
  /**
   * Opt-in features switched on for this account (e.g. `meta-ads`). Fail-
   * CLOSED under the same freshness rule as `explicit`: empty until a fresh
   * `/account/modules` fetch, so a stale seed never keeps one on.
   */
  enabledFeatures: string[];
  /** A fresh `/account/modules` fetch succeeded (see `isModulesFresh`). */
  isFresh: boolean;
  isLoading: boolean;
  /**
   * Not fresh, the latest fetch failed and none is running. The query has
   * `retry: false`, so a fail-closed surface waiting on `isFresh` would wait
   * forever; it should say so and offer `retry`.
   */
  loadFailed: boolean;
  /** Fetch `/account/modules` again. */
  retry: () => void;
}

/**
 * Keys of every module the account has EXPLICITLY on — the fail-closed
 * `explicit` signal for all modules at once, under the same freshness rule.
 * Empty while loading, on error, or when only the stored seed is available.
 * Stable across renders until the answer changes, so it can key a useMemo.
 */
export function useExplicitModuleKeys(): string[] {
  const query = useAccountModules();
  const fresh = isModulesFresh(query);
  const modules = query.data?.modules;
  return useMemo(
    () => (fresh ? (modules ?? []).filter((m) => m.explicit === true).map((m) => m.key) : []),
    [fresh, modules],
  );
}

/** Access state for one module by key (lowercased title, e.g. "marketing"). */
export function useModuleAccess(moduleKey: string): ModuleAccess {
  const query = useAccountModules();
  const { data, isLoading } = query;
  const fresh = isModulesFresh(query);
  const module: AccountModule | undefined = data?.modules?.find((m) => m.key === moduleKey);
  return {
    enabled: module ? module.enabled : true,
    explicit: isModulesFresh(query) && module?.explicit === true,
    explicitEvenIfStale: module?.explicit === true,
    disabledSections: module?.disabledSections ?? [],
    enabledFeatures: isModulesFresh(query) ? module?.enabledFeatures ?? [] : [],
    isFresh: fresh,
    isLoading,
    loadFailed: !fresh && query.isError && !query.isFetching,
    retry: () => { void query.refetch(); },
  };
}
